import { useState, useEffect } from 'react';
import apiFetch from "./../apiClient";

interface Investment {
  id: string;
  amount: number;
  category: string;
  date: string;
  description: string;
  paymentMethod: string;
}

const PAYMENT_METHODS = ['Dinheiro', 'Débito', 'Crédito', 'PIX', 'Transferência'];

export function AddInvestmentCard({categories, defaultMonth, defaultYear, editingInvestment, onSaved, onCancel}) {
    const defaultDate = `${defaultYear}-${String((defaultMonth ?? new Date().getMonth()) + 1).padStart(2,'0')}-01`;
    const [investmentForm, setInvestmentForm] = useState({ amount:'', displayAmount:'', category:'', date: defaultDate, description:'', paymentMethod:'' });
    const [error, setError] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        if (editingInvestment) {
            setInvestmentForm({
                amount: editingInvestment.transacao_valor,
                displayAmount: String(editingInvestment.transacao_valor),
                category: editingInvestment.categoria_id,
                date: (editingInvestment.transacao_data || '').slice(0, 10),
                description: editingInvestment.transacao_descricao || '',
                paymentMethod: editingInvestment.transacao_forma_de_pagamento || ''
            });
        }
    }, [editingInvestment]);

    const addInvestments = async () => {
        const createInvestment: Investment = {
            id: Date.now().toString(),
            amount: parseFloat(investmentForm.amount),
            category: investmentForm.category,
            date: investmentForm.date,
            description: investmentForm.description || null,
            payment_method: investmentForm.paymentMethod || null
        };
        setIsProcessing(true);
        setError(null);
        try {
            if (editingInvestment) {
                await apiFetch(`/api/financecontrol/update-investment/${editingInvestment.transacao_id}`, {
                    method: "PUT",
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(createInvestment),
                });
            } else {
                await apiFetch("/api/financecontrol/create-investment", {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(createInvestment),
                });
            }
            resetInvestmentForm();
            await onSaved?.();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsProcessing(false);
        }
    };

    const resetInvestmentForm = () => {
        setInvestmentForm({
            amount: '',
            displayAmount: '',
            category: '',
            date: '',
            description: '',
            paymentMethod: ''
        });
    };

    const handleAmountChange = (e) => {
        const valorOriginal = e.target.value;
        const valorTratado = valorOriginal.replace(/[^0-9,.]/g, '');
        const valorNumerico = valorTratado.replace(/\./g, '').replace(',', '.');

        setInvestmentForm({
            ...investmentForm,
            // Armazena a string que será exibida no input (ex: "1.234,56")
            displayAmount: valorTratado,
            // Armazena o valor que será enviado para o backend/cálculo (ex: 1234.56)
            amount: parseFloat(valorNumerico) || 0
        });
    }
    return (
        <div className="row g-3">
            <div className="col-12">
                <label htmlFor="investment-category" className="form-label text-muted-custom small">Categoria</label>
                <select
                    id="investment-category"
                    className="form-select form-select-custom"
                    value={investmentForm ? investmentForm.category : ''}
                    onChange={(e) => setInvestmentForm({ ...investmentForm, category: e.target.value})}
                    onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                >
                    <option value="" disabled>Categoria</option>
                    {categories && categories.filter(cat => cat.categoria_tipo === 'investimento').map((category) => (
                        <option key={category.categoria_id} value={category.categoria_id}>{category.categoria_nome}</option>
                    ))}
                </select>
            </div>
            <div className="col-12">
                <label className="form-label text-muted-custom small">Valor</label>
                <div className="input-group">
                    <span
                        className="input-group-text"
                        style={{
                        backgroundColor: 'var(--color-surface)',
                        border: '1px solid var(--color-border)',
                        borderRight: 'none',
                        color: 'var(--color-text-secondary)'
                        }}
                    >
                        R$
                    </span>
                    <input
                        type='text'
                        className="form-control"
                        placeholder="0,00"
                        value={investmentForm != null ? investmentForm.displayAmount : ''}
                        onChange={handleAmountChange}
                        style={{
                            backgroundColor: 'var(--color-surface)',
                            border: '1px solid var(--color-border)',
                            borderLeft: 'none',
                            color: 'var(--color-text-primary)',
                            padding: '0.75rem 1rem',
                            borderRadius: '0 0.5rem 0.5rem 0'
                        }}
                        onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                        onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                    />
                </div>
            </div>
            <div className="col-12">
                <label className="form-label text-muted-custom small">Data</label>
                <input
                    type="date"
                    className="form-control"
                    value={investmentForm != null ? investmentForm.date : ''}
                    onChange={(e) => setInvestmentForm({ ...investmentForm, date: e.target.value })}
                    style={{
                        backgroundColor: 'var(--color-surface)',
                        border: '1px solid var(--color-border)',
                        color: 'var(--color-text-primary)',
                        padding: '0.75rem 1rem',
                        borderRadius: '0.5rem'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                />
            </div>
            <div className="col-12">
                <label htmlFor="investment-description" className="form-label text-muted-custom small">Descrição (opcional)</label>
                <input
                    id="investment-description"
                    type="text"
                    className="form-control"
                    placeholder="Ex: Tesouro Direto"
                    value={investmentForm != null ? investmentForm.description : ''}
                    onChange={(e) => setInvestmentForm({ ...investmentForm, description: e.target.value })}
                    style={{
                        backgroundColor: 'var(--color-surface)',
                        border: '1px solid var(--color-border)',
                        color: 'var(--color-text-primary)',
                        padding: '0.75rem 1rem',
                        borderRadius: '0.5rem'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                />
            </div>
            <div className="col-12">
                <label htmlFor="investment-payment-method" className="form-label text-muted-custom small">Forma de pagamento (opcional)</label>
                <select
                    id="investment-payment-method"
                    className="form-select form-select-custom"
                    value={investmentForm != null ? investmentForm.paymentMethod : ''}
                    onChange={(e) => setInvestmentForm({ ...investmentForm, paymentMethod: e.target.value })}
                    onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                >
                    <option value="">Não informado</option>
                    {PAYMENT_METHODS.map((method) => (
                        <option key={method} value={method}>{method}</option>
                    ))}
                </select>
            </div>
            <div className="col-12">
                <div className="d-flex gap-2 justify-content-end">
                    <button
                        className="btn btn-outline-custom"
                        onClick={() => {
                            resetInvestmentForm();
                            onCancel?.();
                        }}
                    >
                        Cancelar
                    </button>
                    <button
                        className="btn btn-primary-custom"
                        onClick={addInvestments}
                        disabled={isProcessing}
                    >
                        {editingInvestment ? 'Atualizar' : 'Adicionar'} Investimento
                    </button>
                </div>
                {error && (
                    <small className="text-danger-custom d-block mt-1 text-end">{error}</small>
                )}
            </div>
        </div>
    );
}
